import React, { useCallback, useEffect, useRef, useState } from "react"
import "./themeCv.scss"
import { useMutation, useQuery, useQueryClient } from "react-query"
import {
	Button,
	Tooltip,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	styled,
	Alert,
} from "@mui/material"
import { useReactToPrint } from "react-to-print"
import { useLocation, useNavigate } from "react-router-dom"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import PrintIcon from "@mui/icons-material/Print"
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined"
import AppRegistrationOutlinedIcon from "@mui/icons-material/AppRegistrationOutlined"
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined"
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined"
import AddAPhotoOutlinedIcon from "@mui/icons-material/AddAPhotoOutlined"
import NoPhotographyOutlinedIcon from "@mui/icons-material/NoPhotographyOutlined"
import AutoFixNormalOutlinedIcon from "@mui/icons-material/AutoFixNormalOutlined"
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined"
import VisibilityIcon from "@mui/icons-material/Visibility"
import Swal from "sweetalert2"
import InfoCv from "./themeOne/info/infoCv"
import AdjectifCv from "./themeOne/adjectif/adjectifCv"
import InteretCv from "./themeOne/interet/interetCv"
import ThemeCvDto from "../../../api/models/cvTheme"
import cvThemeService from "../../../api/services/cvThemeService"
import cvskillService from "../../../api/services/cvskillService"
import CvSkillDto from "../../../api/models/cvskill"
import userService from "../../../api/services/userService"
import ObjetCv from "./themeOne/objet/objetCv"
import ThemeTwo from "./themeTwo/themeTwo"

function ThemeCv() {
	const navigate = useNavigate()
	const location = useLocation()
	const state = location.state as any

	// 🔒 Récupérer le paramètre readOnly depuis le state
	const { readOnly } = state || {}
	const isReadOnly = readOnly === true

	const [theme, setTheme] = useState<ThemeCvDto | null>(null)
	const [themeExists, setThemeExists] = useState<boolean | undefined>(false)
	const [themeToModify, setThemeToModify] = useState<boolean | undefined>(
		false,
	)
	const fileInputRef = useRef<HTMLInputElement>(null)
	const [withPhoto, setWithPhoto] = useState(false)
	const printRef = useRef<HTMLDivElement>(null)
	const printRef2 = useRef<HTMLDivElement>(null)
	const [error, setError] = useState<string | null>(null)
	const [isAdmin, setIsAdmin] = useState<boolean>(false)

	const [cvSkill, setCvSkill] = useState<CvSkillDto | null>(null)
	const [isLoading, setIsLoading] = useState(false)
	const [userId, setUserId] = useState<number | null>(null)
	const queryClient = useQueryClient()
	const [cvSkillId, setCvSkillId] = useState<number | undefined>(undefined)

	const { data: themes = [], isError } = useQuery<ThemeCvDto[]>(
		["theme_cv"],
		async () => cvThemeService.getAllCvThemes(),
		{
			keepPreviousData: false,
		},
	)

	// Effet pour charger les données initiales
	useEffect(() => {
		const fetchData = async () => {
			try {
				const storedUserId = localStorage.getItem("selectedUserId")
				if (!storedUserId)
					throw new Error("ID utilisateur sélectionné non trouvé")
				setUserId(Number(storedUserId))
				const userRoles = await userService.getUserRoles()
				setIsAdmin(userRoles.includes("ROLE_ADMIN"))
				const storedCvSkillId =
					localStorage.getItem("selectedCvSkillId")
				if (!storedCvSkillId) return
				const cvSkillData = await cvskillService.getCvSkillById(
					Number(storedCvSkillId),
				)
				setCvSkill(cvSkillData)
				setCvSkillId(cvSkillData?.id)
				setThemeToModify(false)
			} catch (error) {
				setError(
					"Impossible de récupérer les données. Veuillez réessayer.",
				)
			}
		}
		fetchData()
	}, [])

	useEffect(() => {
		if (cvSkill && themes.length > 0) {
			const initialTheme = themes.find(
				t => t.id === Number(cvSkill.themeId),
			)
			if (!initialTheme) setThemeExists(true)
			setTheme(initialTheme || null)
		}
	}, [cvSkill, themes])

	useEffect(() => {
		if (cvSkill) {
			setWithPhoto(!!cvSkill.withPhoto)
		}
	}, [cvSkill])

	const handlePrint1 = useReactToPrint({
		contentRef: printRef,
		documentTitle: "CV Preview",
	})
	const handlePrint2 = useReactToPrint({
		contentRef: printRef2,
		documentTitle: "CV Preview",
	})

	const handlePrint = () => {
		if (theme?.modelCv?.type === "type1") {
			handlePrint1()
		}
		if (theme?.modelCv?.type === "type2") {
			handlePrint2()
		}
	}

	const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()
	}

	const handleThemeChange = (event: SelectChangeEvent<number>) => {
		// 🔒 Bloquer la modification en mode lecture seule
		if (isReadOnly) return

		const selectedThemeId = event.target.value as number
		const selectedTheme =
			themes.find(themeItem => themeItem.id === selectedThemeId) || null
		setTheme(selectedTheme)
	}

	const ActionButton = styled(Button)(({ theme }) => ({
		marginTop: theme.spacing(1),
		width: "48px",
		height: "48px",
		borderColor: "#4caf50",
		color: "#4caf50",
		"&:hover": {
			backgroundColor: "#4caf50",
			color: "white",
		},
	}))

	// Upload photo
	const uploadPhotoMutation = useMutation(
		(file: File) => cvskillService.uploadPhoto(cvSkill?.id!, file),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["photo", cvSkill?.id])
				Swal.fire({
					icon: "success",
					title: "Photo uploadée avec succès",
					showConfirmButton: false,
					timer: 1500,
				})
			},
			onError: () => {
				Swal.fire({
					icon: "error",
					title: "Erreur lors de l'upload de la photo",
					text: "Veuillez réessayer plus tard.",
				})
			},
		},
	)

	const handlePhotoUpload = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			// 🔒 Bloquer l'upload en mode lecture seule
			if (isReadOnly) return

			const file = event.target.files?.[0]
			if (file && cvSkill?.id) {
				uploadPhotoMutation.mutate(file)
			}
		},
		[cvSkill?.id, uploadPhotoMutation, isReadOnly],
	)

	const handleIconClick = () => {
		// 🔒 Bloquer en mode lecture seule
		if (isReadOnly) return
		fileInputRef.current?.click()
	}

	const handleEditLoisirInteret = () => {
		// 🔒 Bloquer en mode lecture seule
		if (isReadOnly) return

		if (cvSkill && cvSkill.user) {
			navigate("/cvSkill/poleloisirsInteret", {
				state: {
					editMode: true,
					userId: cvSkill.user.id,
					poleLoisirInterets: cvSkill.poleLoisirInterets || [],
					poleInterets: cvSkill.poleInterets || [],
				},
			})
		} else if (userId) {
			navigate("/cvSkill/poleloisirsInteret", {
				state: {
					editMode: true,
					cvSkillId,
					userId,
					poleLoisirInterets: cvSkill?.poleLoisirInterets || [],
					poleInterets: cvSkill?.poleInterets || [],
				},
			})
		} else {
			setError(
				"Impossible de modifier les loisirs et intérêts. Données manquantes.",
			)
		}
	}

	const handleEditPersonnalite = () => {
		// 🔒 Bloquer en mode lecture seule
		if (isReadOnly) return

		if (isAdmin) {
			navigate("/cvSkill/polePersonnalite", {
				state: {
					editMode: true,
					cvSkillId: cvSkill?.id,
					userId: cvSkill?.user?.id,
					polePersonnaliteTraits: cvSkill?.polePersonnaliteTraits,
					polePersonnalitesTypes: cvSkill?.polePersonnalitesTypes,
				},
			})
		}
	}

	const handleEditPersonnalite2 = () => {
		// 🔒 Bloquer en mode lecture seule
		if (isReadOnly) return

		if (isAdmin) {
			navigate("/cvSkill/polePersonnalite2", {
				state: {
					editMode: true,
					cvSkillId: cvSkill?.id,
					userId: cvSkill?.user?.id,
					polePersonnalitesTypes: cvSkill?.polePersonnalitesTypes,
				},
			})
		}
	}

	const handleEditAtouts = () => {
		// 🔒 Bloquer en mode lecture seule
		if (isReadOnly) return

		if (isAdmin) {
			navigate("/cvSkill/poleAtouts", {
				state: {
					editMode: true,
					cvSkillId: cvSkill?.id,
					userId: cvSkill?.user?.id,
					poleAtouts: cvSkill?.poleAtouts,
				},
			})
		}
	}

	const handleEditInterets = () => {
		// 🔒 Bloquer en mode lecture seule
		if (isReadOnly) return

		if (isAdmin) {
			navigate("/cvSkill/poleInterets", {
				state: {
					editMode: true,
					cvSkillId: cvSkill?.id,
					userId: cvSkill?.user?.id,
					poleInterets: cvSkill?.poleInterets,
				},
			})
		}
	}

	// Mutation pour mettre à jour un CV Skill existant
	const updateCvSkillMutation = useMutation(
		(updatedCvSkill: Partial<CvSkillDto>) => {
			if (!cvSkill?.id || !userId)
				throw new Error("CV Skill ID ou User ID invalide")
			return cvskillService.updateCvSkill(
				cvSkill?.id,
				updatedCvSkill as CvSkillDto,
				userId,
			)
		},
		{
			onMutate: () => setIsLoading(true),
			onSuccess: () => {
				Swal.fire({
					icon: "success",
					title: "Thème mis à jour avec succès",
					showConfirmButton: false,
					timer: 1500,
				})
			},
			onError: (error: any) => {
				setError(
					error.response?.data?.message ||
						"Erreur lors de la mise à jour. Veuillez réessayer.",
				)
			},
			onSettled: () => setIsLoading(false),
		},
	)

	const handleSave = async () => {
		// 🔒 Bloquer la sauvegarde en mode lecture seule
		if (isReadOnly) {
			console.warn(
				"[ThemeCv] Modification interdite en mode lecture seule",
			)
			return
		}

		if (!userId) {
			setError("ID utilisateur manquant. Impossible de continuer.")
			setThemeExists(true)
			setThemeToModify(false)
			return
		}
		try {
			const updatedCvSkill: Partial<CvSkillDto> = {
				...cvSkill,
				withPhoto,
				themeId: String(theme?.id),
			}
			updateCvSkillMutation.mutate(updatedCvSkill)
			setThemeToModify(false)
		} catch {
			setError("Erreur lors de la mise à jour. Veuillez réessayer.")
		}
	}

	// delete the CV
	const deleteCvSkillMutation = useMutation(
		(id: number) => cvskillService.deleteCvSkill(id),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["cvSkill", cvSkillId, userId])
				localStorage.removeItem("selectedCvSkillId")
				Swal.fire({
					icon: "success",
					title: "CV Skill supprimé avec succès",
					showConfirmButton: false,
					timer: 1500,
				})
				navigate("/companies")
			},
			onError: () => {
				Swal.fire({
					icon: "error",
					title: "Erreur lors de la suppression du CV Skill",
					text: "Veuillez réessayer plus tard.",
				})
			},
		},
	)

	const handleDeleteCvSkill = () => {
		// 🔒 Bloquer la suppression en mode lecture seule
		if (isReadOnly) return

		if (isAdmin) {
			Swal.fire({
				title: "Êtes-vous sûr ?",
				text: "Cette action est irréversible !",
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: "#3085d6",
				cancelButtonColor: "#d33",
				confirmButtonText: "Oui, supprimer",
				cancelButtonText: "Annuler",
			}).then(result => {
				if (result.isConfirmed && cvSkillId) {
					deleteCvSkillMutation.mutate(cvSkillId)
				}
			})
		}
	}

	const renderCvContent = () => {
		const type = theme?.modelCv?.type
		if (!theme?.modelCv?.type) return null
		if (type === "type1") {
			return (
				<div ref={printRef} className="offer-page">
					<Grid container className="cv-section">
						<InfoCv isPhoto={withPhoto} theme={theme} />
						<ObjetCv theme={theme} />
						<AdjectifCv theme={theme} />
						<InteretCv theme={theme} />
					</Grid>
				</div>
			)
		}

		if (type === "type2") {
			return (
				<div ref={printRef2} className="offer-page2">
					<ThemeTwo isPhoto={withPhoto} theme={theme} />
				</div>
			)
		}

		return <div>Type de CV inconnu</div>
	}

	return (
		<section className="page offer-page">
			<section className="content offer-container">
				<div className="offer-list-content ">
					<Grid container size={12} sx={{ width: "100%" }}>
						{/* 🔒 Message en mode lecture seule */}
						{isReadOnly && (
							<Alert
								severity="info"
								icon={<VisibilityIcon />}
								sx={{
									width: "100%",
									marginBottom: 2,
									backgroundColor: "#e3f2fd",
									"& .MuiAlert-icon": {
										color: "#1976d2",
									},
								}}
							>
								<strong>Mode Visualisation</strong>
								<br />
								Vous consultez votre CV en lecture seule. Les
								modifications ne sont pas possibles. Pour toute
								demande de modification, veuillez contacter
								l'administration.
							</Alert>
						)}

						{/* Formulaire de recherche */}
						<form
							onSubmit={handleSearch}
							style={{
								marginBottom: "20px",
								marginTop: "10px",
								width: "100%",
							}}
						>
							<Grid container spacing={2}>
								<Grid
									size={3}
									sx={{
										display: "flex",
										justifyContent: "flex-start",
									}}
								>
									<Tooltip title="Revenir à la première page">
										<ActionButton
											onClick={() =>
												navigate("/companies")
											}
										>
											<ArrowBackIcon />
										</ActionButton>
									</Tooltip>

									{/* 🔒 Cacher les boutons d'édition en mode lecture seule */}
									{!isReadOnly && (
										<>
											<Tooltip title="Mettre à jour les loisirs et intérêts">
												<ActionButton
													onClick={
														handleEditLoisirInteret
													}
												>
													<AppRegistrationOutlinedIcon />
												</ActionButton>
											</Tooltip>
											<Tooltip title="Mettre à jour les traits">
												<ActionButton
													onClick={() => {
														handleEditPersonnalite2()
														handleEditPersonnalite()
													}}
												>
													<DrawOutlinedIcon />
												</ActionButton>
											</Tooltip>
											<Tooltip title="Mettre à jour les atouts">
												<ActionButton
													onClick={handleEditAtouts}
												>
													<EditNoteOutlinedIcon />
												</ActionButton>
											</Tooltip>
											<Tooltip title="Mettre à jour les intérêts">
												<ActionButton
													onClick={handleEditInterets}
												>
													<AutoFixNormalOutlinedIcon />
												</ActionButton>
											</Tooltip>
										</>
									)}
								</Grid>
								<Grid size={6}>
									<FormControl fullWidth>
										<InputLabel id="theme-label">
											Choisissez un thème
										</InputLabel>
										<Select
											labelId="theme-label"
											value={theme ? theme.id : ""}
											onChange={handleThemeChange}
											disabled={
												isReadOnly ||
												!(themeExists || themeToModify)
											}
											required
										>
											<MenuItem value="">
												<em>Sélectionnez un thème</em>
											</MenuItem>
											{isLoading && (
												<MenuItem disabled>
													Chargement...
												</MenuItem>
											)}
											{isError && (
												<MenuItem disabled>
													Erreur lors du chargement
													des thèmes
												</MenuItem>
											)}
											{!isLoading &&
												!isError &&
												themes.map(themeItem => (
													<MenuItem
														key={themeItem.id}
														value={themeItem.id}
													>
														{themeItem.name}
													</MenuItem>
												))}
										</Select>
									</FormControl>
								</Grid>
								<Grid
									size={3}
									sx={{
										display: "flex",
										justifyContent: "flex-end",
									}}
								>
									{/* 🔒 Cacher les boutons de modification en mode lecture seule */}
									{!isReadOnly && (
										<>
											<Tooltip
												title={
													!withPhoto
														? "Inclure une photo dans le CV"
														: "Enlever la photo"
												}
											>
												<span>
													<ActionButton
														onClick={() =>
															setWithPhoto(
																prev => !prev,
															)
														}
														disabled={
															!themeToModify
														}
													>
														{!withPhoto ? (
															<AddAPhotoOutlinedIcon />
														) : (
															<NoPhotographyOutlinedIcon />
														)}
													</ActionButton>
												</span>
											</Tooltip>

											<input
												type="file"
												ref={fileInputRef}
												onChange={handlePhotoUpload}
												style={{ display: "none" }}
												accept="image/*"
											/>
											<Tooltip title="Ajouter une nouvelle photo dans le CV">
												<ActionButton
													onClick={handleIconClick}
													disabled={!themeToModify}
												>
													<AddPhotoAlternateOutlinedIcon fontSize="large" />
												</ActionButton>
											</Tooltip>
											{!themeToModify && (
												<Tooltip title="Modifier le thème">
													<ActionButton
														onClick={() =>
															setThemeToModify(
																true,
															)
														}
													>
														<EditOutlinedIcon />
													</ActionButton>
												</Tooltip>
											)}

											{themeToModify && (
												<Tooltip title="Enregistrer le thème">
													<ActionButton
														onClick={handleSave}
													>
														<SaveOutlinedIcon />
													</ActionButton>
												</Tooltip>
											)}
										</>
									)}

									{/* ✅ Bouton Imprimer TOUJOURS visible */}
									<Tooltip title="Imprimer le CV">
										<ActionButton
											onClick={() => handlePrint()}
										>
											<PrintIcon />
										</ActionButton>
									</Tooltip>

									{/* 🔒 Bouton Supprimer uniquement pour admin et pas en lecture seule */}
									{isAdmin && !isReadOnly && (
										<Tooltip title="Supprimer le CV">
											<ActionButton
												onClick={handleDeleteCvSkill}
											>
												<DeleteForeverOutlinedIcon />
											</ActionButton>
										</Tooltip>
									)}
								</Grid>
							</Grid>
						</form>

						{renderCvContent()}
					</Grid>
				</div>
			</section>
		</section>
	)
}
export default ThemeCv
